// src/pages/MatchSetup/Caps.js
//
// Inline SVG baseball-cap icons for season-leader badges.
//
// Four colors:
//   orange — most runs this season
//   purple — most wickets this season
//   green  — best wicket-keeper this season (most stumpings + caught-behind)
//   blue   — best outfield fielder this season (highest fielding_pts among non-keepers)
//
// Blue rendering is wired on the frontend now; the backend split for
// green vs blue ships in the next session (along with the caught_behind
// migration + backfill).
//
// Use:
//   <CapBadge kind="orange" />
//   <CapBadge kind="purple" />
//   <CapBadge kind="green" />
//   <CapBadge kind="blue" />

import React from 'react';

const COLORS = {
  orange: { fill: '#F97316', stroke: '#C2410C', label: 'Orange Cap — most runs this season' },
  purple: { fill: '#9333EA', stroke: '#6B21A8', label: 'Purple Cap — most wickets this season' },
  green:  { fill: '#16A34A', stroke: '#15803D', label: 'Green Cap — best wicket-keeper this season' },
  blue:   { fill: '#2563EB', stroke: '#1E40AF', label: 'Blue Cap — best outfield fielder this season' },
};

export function CapBadge({ kind, size = 16 }) {
  const c = COLORS[kind];
  if (!c) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ verticalAlign: 'middle', flexShrink: 0 }}
    >
      <title>{c.label}</title>
      {/* Cap crown — rounded dome */}
      <path
        d="M4 14 C4 9, 7 5.5, 12 5.5 C17 5.5, 20 9, 20 14 L20 15 L4 15 Z"
        fill={c.fill}
        stroke={c.stroke}
        strokeWidth="0.8"
      />
      {/* Cap peak — extends right */}
      <path
        d="M20 14.5 L23 16 L23 17 L20 17 Z"
        fill={c.fill}
        stroke={c.stroke}
        strokeWidth="0.8"
      />
      {/* Button on top */}
      <circle cx="12" cy="6" r="1" fill={c.stroke} />
      {/* Subtle band */}
      <path
        d="M4 14 L20 14"
        stroke={c.stroke}
        strokeWidth="0.6"
        opacity="0.6"
      />
    </svg>
  );
}

// Builds Map<member_id, ['orange'|'purple'|'green'|'blue', ...]>
// from setup-state cap-holder fields. Backend currently sends green_cap_member_id
// only; once the split lands, blue_cap_member_id will be populated and this
// helper picks it up automatically.
export function makeCapBadgeMap({
  orange_cap_member_id,
  purple_cap_member_id,
  green_cap_member_id,
  blue_cap_member_id,
} = {}) {
  const map = new Map();
  if (orange_cap_member_id) {
    map.set(orange_cap_member_id, [...(map.get(orange_cap_member_id) || []), 'orange']);
  }
  if (purple_cap_member_id) {
    map.set(purple_cap_member_id, [...(map.get(purple_cap_member_id) || []), 'purple']);
  }
  if (green_cap_member_id) {
    map.set(green_cap_member_id, [...(map.get(green_cap_member_id) || []), 'green']);
  }
  if (blue_cap_member_id) {
    map.set(blue_cap_member_id, [...(map.get(blue_cap_member_id) || []), 'blue']);
  }
  return map;
}
